// Suggest a partition only when it fully explains the quantity order (complete, grouped, contiguous, clean).
import { sizeFamilyOf } from "@CommonUtils/cdtStudio/domain/sizeFamily";
import { isPlaceholderValue, type StudioItem } from "./studioTypes";

export interface OrderExplanation {
  attr: string;
  values: readonly string[];
  blockShare: number;
}

interface Placed {
  lane: string;
  x: number;
  value: string;
  magnitude: number;
  family: string;
  widthCm: number;
}

const placementsOf = (
  members: readonly StudioItem[],
  attr: string,
  quantityAttribute: string,
): Placed[] => {
  const out: Placed[] = [];
  for (const item of members) {
    const raw = item.attrs[attr];
    const value = raw == null ? "" : String(raw);
    const quantity = sizeFamilyOf(String(item.attrs[quantityAttribute] ?? ""));
    for (const placement of item.placements) {
      out.push({
        lane: `${placement.run}|${placement.shelf}`,
        x: placement.xCm,
        value,
        magnitude: quantity.magnitude,
        family: quantity.family,
        widthCm: placement.widthCm,
      });
    }
  }
  return out.sort((a, b) => (a.lane < b.lane ? -1 : a.lane > b.lane ? 1 : a.x - b.x));
};

export const explanationBy = (
  members: readonly StudioItem[],
  attr: string,
  quantityAttribute: string,
): OrderExplanation | null => {
  if (members.length < 3) return null;
  const placed = placementsOf(members, attr, quantityAttribute);
  if (placed.length === 0) return null;
  if (placed.some((entry) => isPlaceholderValue(entry.value))) return null;
  const distinct = new Set(placed.map((entry) => entry.value));
  if (distinct.size < 2 || distinct.size >= placed.length) return null;

  const byLane = new Map<string, Placed[]>();
  for (const entry of placed) byLane.set(entry.lane, [...(byLane.get(entry.lane) ?? []), entry]);

  let widthInMainStretch = 0;
  let totalWidth = 0;
  const seenOrder: string[] = [];
  for (const lane of byLane.values()) {
    const stretchWidth = new Map<string, number[]>();
    let current = "";
    for (const entry of lane) {
      if (entry.value !== current) {
        const runs = stretchWidth.get(entry.value);
        if (runs && runs.length > 0) return null;
        stretchWidth.set(entry.value, [0]);
        current = entry.value;
        if (!seenOrder.includes(entry.value)) seenOrder.push(entry.value);
      }
      const runs = stretchWidth.get(entry.value) as number[];
      runs[runs.length - 1] += entry.widthCm;
    }
    let previous: Placed | null = null;
    for (const entry of lane) {
      if (previous && previous.value === entry.value && previous.family === entry.family) {
        if (entry.magnitude < previous.magnitude) return null;
      }
      previous = entry;
    }
    for (const runs of stretchWidth.values()) {
      const total = runs.reduce((a, b) => a + b, 0);
      widthInMainStretch += Math.max(...runs);
      totalWidth += total;
    }
  }
  if (totalWidth <= 0) return null;
  return {
    attr,
    values: seenOrder,
    blockShare: widthInMainStretch / totalWidth,
  };
};

export const explainOrderViolation = (
  members: readonly StudioItem[],
  candidateAttrs: readonly string[],
  quantityAttribute: string,
): OrderExplanation | null => {
  let best: OrderExplanation | null = null;
  for (const attr of candidateAttrs) {
    if (attr === quantityAttribute) continue;
    const explanation = explanationBy(members, attr, quantityAttribute);
    if (!explanation) continue;
    if (!best || explanation.blockShare > best.blockShare) best = explanation;
  }
  return best;
};
